const TEXT_ANALYSIS_LEVEL_ROUTES = ['extraction', 'keyword', 'summary']

const PROJECT_MENU_ROUTE_CONFIG = {
  dashboard: {
    routeName: 'DashBoard',
    getPath: projectPk => `/dashboard/${projectPk}`,
  },
  'data-management': {
    routeName: 'DataManagement',
    getPath: projectPk => `/data-management/${projectPk}`,
  },
  process: {
    routeName: 'Process',
    getPath: projectPk => `/process/${projectPk}`,
  },
  work: {
    routeName: 'WorkManagement',
    getPath: projectPk => `/work-management/${projectPk}`,
  },
  rpa2: {
    routeName: 'RpaList',
    getPath: projectPk => `/rpa/${projectPk}`,
  },
  estimate: {
    routeName: 'FormList',
    getPath: projectPk => `/form-list/${projectPk}`,
  },
  table: {
    routeName: 'Table',
    getPath: projectPk => `/table/${projectPk}`,
  },
  insight: {
    routeName: 'InsightList',
    getPath: projectPk => `/insight-list/${projectPk}`,
  },
  'ai-engine': {
    routeName: 'AiAgent',
    getPath: projectPk => `/ai-agent/${projectPk}`,
  },
  'email-verify': {
    routeName: 'EmailVerify',
    getPath: projectPk => `/email-verify/${projectPk}`,
  },
  'table-detection': {
    routeName: 'TableDetection',
    getPath: projectPk => `/table-detection/${projectPk}`,
  },
  'detection-result': {
    routeName: 'DetectionResult',
    getPath: projectPk => `/detection-result/${projectPk}`,
  },
  'inner-db': {
    routeName: 'InnerDb',
    getPath: projectPk => `/inner-db/${projectPk}`,
  },
  ocr: {
    routeName: 'Ocr',
    getPath: projectPk => `/ocr/${projectPk}`,
  },
  'pdf-classification': {
    routeName: 'PdfClassification',
    getPath: projectPk => `/pdf-classification/${projectPk}`,
  },
  'mou-list': {
    routeName: 'MouList',
    getPath: projectPk => `/mou-list/${projectPk}`,
  },
  'corporate-sales-status': {
    routeName: 'CorporateSalesStatus',
    getPath: projectPk => `/corporate-sales-status/${projectPk}`,
  },
  'iros-search': {
    routeName: 'IrosSearch',
    getPath: projectPk => `/iros-search/${projectPk}`,
  },
  slicer: {
    routeName: 'DataGrouping',
    getPath: projectPk => `/data-grouping/${projectPk}`,
  },
  scatter: {
    routeName: 'Scatter',
    getPath: () => '/scatter',
    requiresProject: false,
  },
}

const ACTIVE_ROUTE_ALIASES = {
  WorkManagement: [
    'WorkManagementLayout',
    'WorkManagement',
    'MyWorkItems',
    'WorkNotifications',
    'WorkExternalEvents',
    'WorkDetail',
    'WorkItemRedirect',
    'WorkTemplateLayout',
    'WorkTemplateList',
    'WorkTemplateCreate',
    'WorkTemplateDetail',
    'WorkTemplatePublish',
    'RuntimeDefinitionList',
    'RuntimeDefinitionCreate',
    'RuntimeDefinitionDetail',
  ],
}

function isActiveRoute(menuRouteName, activeRouteName) {
  if (!menuRouteName || !activeRouteName) return false
  if (menuRouteName === activeRouteName) return true
  return (ACTIVE_ROUTE_ALIASES[menuRouteName] || []).includes(activeRouteName)
}

TEXT_ANALYSIS_LEVEL_ROUTES.forEach(menuKey => {
  PROJECT_MENU_ROUTE_CONFIG[menuKey] = {
    routeName: menuKey.charAt(0).toUpperCase() + menuKey.slice(1),
    getPath: projectPk => `/${menuKey}/${projectPk}/1`,
  }
})

function hasOwnPermission(permission, menuKey) {
  return !!permission && Object.prototype.hasOwnProperty.call(permission, menuKey)
}

function hasProjectMenuPermission(project) {
  return !!(project && Array.isArray(project.menu_permission) && project.menu_permission.length > 0)
}

function isOwnerOrOwnProject(project) {
  return !!project && !hasProjectMenuPermission(project)
}

function getMatchedProjectPermissionEntries(userInfo, project) {
  if (!userInfo || !hasProjectMenuPermission(project)) {
    return []
  }

  const organizationTree = Array.isArray(userInfo.organization_tree) ? userInfo.organization_tree : []
  const organizationIds = [userInfo.organization_sub, ...organizationTree]
  const result = []

  project.menu_permission.forEach(item => {
    if (!item) return
    if ((item.type === 'organization_user' || item.type === 'owner') && item.id === userInfo.pk) {
      result.push(item)
      return
    }
    if (item.type === 'organization' && organizationIds.includes(item.id)) {
      result.push(item)
    }
  })

  return result
}

function getEntryPermission(entry) {
  if (entry && entry.effective_menu_permission && typeof entry.effective_menu_permission === 'object') {
    return entry.effective_menu_permission
  }
  return entry && entry.menu_permission
}

function isEntryMenuDisabled(entry, menuKey) {
  return !!(entry && Array.isArray(entry.disabled_menu_keys) && entry.disabled_menu_keys.includes(menuKey))
}

function getPreferredProjectPermissionEntry(userInfo, project) {
  const matchedEntries = getMatchedProjectPermissionEntries(userInfo, project)
  if (!matchedEntries.length) {
    return null
  }

  const directEntry = matchedEntries.find(item => item.type === 'organization_user' || item.type === 'owner')
  if (directEntry) {
    return directEntry
  }

  const organizationTree = Array.isArray(userInfo && userInfo.organization_tree) ? userInfo.organization_tree : []
  const organizationIds = [userInfo && userInfo.organization_sub, ...organizationTree]
  return organizationIds
    .map(orgId => matchedEntries.find(item => item.type === 'organization' && item.id === orgId))
    .find(Boolean) || matchedEntries[0]
}

function normalizeMenuOrder(order, fallbackOrder) {
  const fallback = Array.isArray(fallbackOrder) ? fallbackOrder : []
  const fallbackSet = new Set(fallback)
  const result = []

  if (Array.isArray(order)) {
    order.forEach(menuKey => {
      if (typeof menuKey !== 'string') return
      if (!fallbackSet.has(menuKey)) return
      if (result.includes(menuKey)) return
      result.push(menuKey)
    })
  }

  fallback.forEach(menuKey => {
    if (!result.includes(menuKey)) {
      result.push(menuKey)
    }
  })

  return result
}

function getProjectMenuOrder(menuPermissionList, userInfo, project) {
  const fallbackOrder = Array.isArray(menuPermissionList) ? menuPermissionList : []
  const preferredEntry = getPreferredProjectPermissionEntry(userInfo, project)
  const candidateOrders = [
    preferredEntry && preferredEntry.menu_permission_order,
    project && project.menu_permission_order,
    userInfo && userInfo.menu_permission_order,
  ]

  const firstOrder = candidateOrders.find(order => Array.isArray(order) && order.length > 0)
  return normalizeMenuOrder(firstOrder, fallbackOrder)
}

function hasUserMenuKey(menuKey, userInfo) {
  return hasOwnPermission(userInfo && userInfo.menu_permission, menuKey)
}

function isUserMenuEnabled(menuKey, userInfo) {
  return !!(userInfo && userInfo.menu_permission && userInfo.menu_permission[menuKey] === true)
}

function hasProjectMenuKey(menuKey, userInfo, project) {
  if (isOwnerOrOwnProject(project)) {
    return hasUserMenuKey(menuKey, userInfo)
  }

  const matchedEntries = getMatchedProjectPermissionEntries(userInfo, project)
  if (!matchedEntries.length) {
    return hasUserMenuKey(menuKey, userInfo)
  }

  const preferredEntry = getPreferredProjectPermissionEntry(userInfo, project)
  if (!preferredEntry) {
    return hasUserMenuKey(menuKey, userInfo)
  }

  return hasOwnPermission(getEntryPermission(preferredEntry), menuKey) || isEntryMenuDisabled(preferredEntry, menuKey)
}

export function canAccessProjectMenu(menuKey, userInfo, project) {
  if (!menuKey || !isUserMenuEnabled(menuKey, userInfo)) {
    return false
  }

  if (isOwnerOrOwnProject(project)) {
    return true
  }

  const matchedEntries = getMatchedProjectPermissionEntries(userInfo, project)
  if (!matchedEntries.length) {
    return true
  }

  const preferredEntry = getPreferredProjectPermissionEntry(userInfo, project)
  if (!preferredEntry || isEntryMenuDisabled(preferredEntry, menuKey)) {
    return false
  }

  const permission = getEntryPermission(preferredEntry)
  return !!(permission && permission[menuKey] === true)
}

export function getFirstAccessibleProjectMenu(menuPermissionList, userInfo, project) {
  if (!Array.isArray(menuPermissionList)) {
    return null
  }

  return getProjectMenuOrder(menuPermissionList, userInfo, project)
    .find(menuKey => canAccessProjectMenu(menuKey, userInfo, project)) || null
}

export function getProjectMenuRoute(menuKey, projectPk) {
  if (!menuKey) {
    return null
  }

  const routeConfig = PROJECT_MENU_ROUTE_CONFIG[menuKey]
  if (routeConfig) {
    if (routeConfig.requiresProject !== false && !projectPk) {
      return null
    }
    return routeConfig.getPath(projectPk)
  }

  if (!projectPk) {
    return null
  }

  return `/${menuKey}/${projectPk}`
}

export function getProjectMenuRouteName(menuKey) {
  return PROJECT_MENU_ROUTE_CONFIG[menuKey] && PROJECT_MENU_ROUTE_CONFIG[menuKey].routeName
}

export function applyProjectMenuRoutes(items, projectPk) {
  const sourceItems = Array.isArray(items) ? items : []

  return sourceItems.map(item => {
    const copiedItem = { ...item }
    const routeName = getProjectMenuRouteName(copiedItem.value)
    const route = routeName ? getProjectMenuRoute(copiedItem.value, projectPk) : null

    if (route) copiedItem.to = route
    if (routeName) copiedItem.routeName = routeName
    if (Array.isArray(copiedItem.children)) {
      copiedItem.children = applyProjectMenuRoutes(copiedItem.children, projectPk)
    }

    return copiedItem
  })
}

function applyPermissionToMenuItem(item, userInfo, project, showDisabled) {
  const copiedItem = { ...item }
  const menuKey = copiedItem.value
  const accessAllowed = canAccessProjectMenu(menuKey, userInfo, project)
  const visible = showDisabled ? hasProjectMenuKey(menuKey, userInfo, project) : accessAllowed

  copiedItem.menuPermission = visible
  if (showDisabled) {
    copiedItem.disabled = !accessAllowed
  } else if (Object.prototype.hasOwnProperty.call(copiedItem, 'disabled')) {
    copiedItem.disabled = copiedItem.disabled || !accessAllowed
  }

  if (Array.isArray(copiedItem.children)) {
    copiedItem.children = copiedItem.children.map(child => applyPermissionToMenuItem(child, userInfo, project, showDisabled))
  }

  return copiedItem
}

export function applyMenuPermissionsToDrawerItems(items, userInfo, project, options = {}) {
  const showDisabled = !!options.showDisabled
  const activeRouteName = options.activeRouteName
  const sourceItems = Array.isArray(items) ? items : []
  const menuOrder = getProjectMenuOrder(sourceItems.map(item => item.value), userInfo, project)
  const menuOrderIndexMap = menuOrder.reduce((acc, menuKey, index) => {
    acc[menuKey] = index
    return acc
  }, {})

  const permittedItems = sourceItems
    .map(item => applyPermissionToMenuItem(item, userInfo, project, showDisabled))
    .filter(item => item.menuPermission || item.children)
    .map(item => {
      if (item.children) {
        item.children = item.children.filter(child => child.menuPermission)
        if (activeRouteName && item.children.some(child => isActiveRoute(child.routeName, activeRouteName))) {
          item.active = true
          item.childActive = true
        }
      }
      if (item.children && !item.menuPermission) {
        return JSON.parse(JSON.stringify(item.children))
      }
      return item
    })
    .filter(item => !item.children || item.children.length > 0)

  return [].concat(...permittedItems)
    .sort((a, b) => {
      const aIndex = Object.prototype.hasOwnProperty.call(menuOrderIndexMap, a.value) ? menuOrderIndexMap[a.value] : Number.MAX_SAFE_INTEGER
      const bIndex = Object.prototype.hasOwnProperty.call(menuOrderIndexMap, b.value) ? menuOrderIndexMap[b.value] : Number.MAX_SAFE_INTEGER
      return aIndex - bIndex
    })
}
